/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import { useDataFetchIndicator } from '..';

import vopayApi from '../../services/api.config';

type HookProps = {
  api: vp.Api;
  bearer?: string;
};

export type ChangePasswordRequest = {
  emailAddress: string;
  newPassword: string;
  shouldSendWelcomeEmail: boolean;
};

export type ChangePasswordResponse = {
  success: boolean;
  errorMessage: string;
  username: string;
};

type ReturnTuple = [ChangePasswordResponse, (requestParams: ChangePasswordRequest) => void, () => void];

function usePostAccountUsersChangePassword({ api, bearer }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<ChangePasswordRequest>();
  const [response, setResponse] = useState<ChangePasswordResponse>({
    success: false,
    errorMessage: '',
    username: '',
  });
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: ChangePasswordRequest) => {
      setDataFetchingInfo(true, 'updating password', 'fullScreen');
      const { Success, ErrorMessage, Username } = await api.postAccountUsersChangePassword(
        buildParams(requestParams),
        bearer,
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage || '',
        username: Username || '',
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: ChangePasswordRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse({
        success: false,
        errorMessage: '',
        username: '',
      });
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: ChangePasswordRequest): vp.AccountUsersChangePasswordRequest {
    return {
      EmailAddress: requestParams.emailAddress,
      NewPassword: requestParams.newPassword,
      SendEmail: requestParams.shouldSendWelcomeEmail,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostAccountUsersChangePassword);
